import {
  CROSS_APP_LABEL,
  type AppliedLearning,
  type CreativeLearningResource,
  type LearningNarrowingResource,
  type LearningSuggestionResource,
  type NarrowingDecision,
} from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { targetLabel } from '../../app/history.js';
import { api } from '../../shell/api.js';
import { EmptyState } from '../../shell/EmptyState.js';
import { useAsync } from '../../shell/useAsync.js';
import { LearningGrounding } from './LearningCard.js';

/**
 * Creative Learning where it is read rather than managed.
 *
 * Two surfaces, two different questions. The App page shelf is this App's own evidence
 * and nothing else - no other App's Learning reaches it, ever. Beside a Brief the same
 * App comes first and a few cross-App suggestions follow, each one labelled, because
 * carrying a finding from one App to another is a decision worth seeing. Neither surface
 * applies anything on its own: `Use` is a press.
 */

function Provenance({ learning }: { learning: CreativeLearningResource }): ReactNode {
  return (
    <p className="note">{learning.provenance.map((fact) => fact.statement).join(' · ')}</p>
  );
}

/** `What worked before`, on the App page. This App and no other. */
export function LearningShelf({ appId }: { appId: string }): ReactNode {
  const [retired, setRetired] = useState(false);
  const loaded = useAsync(async () => api.readLearnings(appId, retired), [appId, retired]);
  const learnings = loaded.value?.learnings ?? [];
  const narrowings = loaded.value?.narrowings ?? [];

  return (
    <>
      <Narrowings appId={appId} narrowings={narrowings} onAnswered={() => loaded.reload()} />
      {learnings.length === 0 ? (
        <EmptyState
          title="Evidence, не правила."
          note="Creative Learning по этому App ещё нет. Learning извлекается из Closed Experiment с Outcome и evidence, и здесь показывается только Learning этого App."
        />
      ) : (
        <ul className="learning-shelf-list" aria-label="Что работало раньше">
          {learnings.map((learning) => (
            <li key={learning.id} className="chip">
              <span>{learning.statement}</span>
              {/* R148: what this observation is grounded in, where it is read. */}
              <LearningGrounding learning={learning} />
              <Provenance learning={learning} />
              {learning.mixedSignal ? <span className="note">Смешанный сигнал</span> : null}
              {learning.state === 'retired' ? <span className="note">Снято</span> : null}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="ghost" onClick={() => setRetired((shown) => !shown)}>
        {retired ? 'Скрыть снятое Creative Learning' : 'Показать снятое Creative Learning'}
      </button>
    </>
  );
}

/**
 * A narrower reading, proposed and waiting.
 *
 * Two Experiments on this App disagreed in different contexts, which is not a refutation
 * but a boundary. The Studio says where the older finding held and where it did not, and
 * then stops: accepting rewords it, splitting keeps both as separate Learnings, leaving
 * it changes nothing. All three keep every piece of evidence behind both sides.
 */
function Narrowings({
  appId,
  narrowings,
  onAnswered,
}: {
  appId: string;
  narrowings: readonly LearningNarrowingResource[];
  onAnswered: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  if (narrowings.length === 0) return null;

  const decide = (narrowingId: string, decision: NarrowingDecision): void => {
    setBusy(true);
    void api
      .resolveNarrowing(narrowingId, decision, appId)
      .then(onAnswered)
      .finally(() => setBusy(false));
  };

  return (
    <ul className="narrowing-proposals" aria-label="Предложенное сужение">
      {narrowings.map((narrowing) => (
        <li key={narrowing.id} className="chip">
          <span>{narrowing.proposedStatement}</span>
          <p className="note">
            {`Позже Experiment в ${targetLabel(narrowing.contextTarget)} нашёл обратное. Пока ничего не изменилось.`}
          </p>
          <button type="button" className="ghost" disabled={busy} onClick={() => decide(narrowing.id, 'accept')}>
            Сузить это Creative Learning
          </button>
          <button type="button" className="ghost" disabled={busy} onClick={() => decide(narrowing.id, 'split')}>
            Оставить оба как отдельные Creative Learning
          </button>
          <button type="button" className="ghost" disabled={busy} onClick={() => decide(narrowing.id, 'leave')}>
            Оставить как есть
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Suggestions beside a Brief. Nothing is applied until `Use`, and `Ignore` reaches this
 * Experiment and nothing else - it removes the suggestion here and teaches no preference
 * anywhere.
 */
export function BriefSuggestions({
  experimentId,
  applied,
  onUsed,
}: {
  experimentId: string;
  applied: readonly AppliedLearning[];
  onUsed: () => void;
}): ReactNode {
  const loaded = useAsync(async () => api.readSuggestions(experimentId), [experimentId]);
  const [taken, setTaken] = useState<readonly string[]>([]);
  const [ignored, setIgnored] = useState<readonly LearningSuggestionResource[]>([]);
  const [busy, setBusy] = useState(false);
  const reload = loaded.reload;

  const suggestions = (loaded.value?.suggestions ?? []).filter(
    (suggestion) =>
      !taken.includes(suggestion.learning.id) &&
      !ignored.some((one) => one.learning.id === suggestion.learning.id) &&
      !applied.some((one) => one.learningId === suggestion.learning.id),
  );

  if (suggestions.length === 0 && ignored.length === 0) {
    return applied.length === 0 ? (
      <EmptyState
        title="Пока нечего развивать."
        note="Creative Learning для подсказки ещё нет. Learning извлекается из Closed Experiment этого App с Outcome и evidence и применяется здесь через Use."
      />
    ) : null;
  }

  /**
   * The way back.
   *
   * R151 names three actions and the third one has to be reversible: `Ignore` here is
   * this Experiment changing its mind, not a preference being taught, so an operator
   * who ignores a suggestion by mistake has to be able to undo it in the same breath.
   * The row stays on the page, moved rather than deleted, and bringing it back removes
   * the ignore on the server and asks for the suggestions again.
   */
  const unignore = (suggestion: LearningSuggestionResource): void => {
    setBusy(true);
    void api
      .unignoreLearning(experimentId, suggestion.learning.id)
      .then(() => {
        setIgnored((held) => held.filter((one) => one.learning.id !== suggestion.learning.id));
        reload();
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      {suggestions.length === 0 ? null : (
        <ul className="learning-suggestions" aria-label="Предложенное Creative Learning">
          {suggestions.map((suggestion) => (
            <li key={suggestion.learning.id} className="chip">
              <span>{suggestion.learning.statement}</span>
              {suggestion.scope === 'crossApp' ? (
                <span className="note">{`${CROSS_APP_LABEL} · ${suggestion.learning.appDisplayName}`}</span>
              ) : null}
              <Provenance learning={suggestion.learning} />
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void api
                    .useLearning(experimentId, { learningId: suggestion.learning.id, text: suggestion.learning.statement })
                    .then(() => {
                      setTaken((held) => [...held, suggestion.learning.id]);
                      onUsed();
                    })
                    .finally(() => setBusy(false));
                }}
              >
                {`Взять ${suggestion.learning.statement}`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void api
                    .ignoreLearning(experimentId, suggestion.learning.id)
                    .then(() => setIgnored((held) => [...held, suggestion]))
                    .finally(() => setBusy(false));
                }}
              >
                {`Игнорировать ${suggestion.learning.statement} здесь`}
              </button>
            </li>
          ))}
        </ul>
      )}

      {ignored.length === 0 ? null : (
        <ul className="learning-ignored" aria-label="Creative Learning, игнорируемое здесь">
          {ignored.map((suggestion) => (
            <li key={suggestion.learning.id} className="chip">
              <span>{suggestion.learning.statement}</span>
              <span className="note">Игнорируется в этом Experiment. Снаружи ничего не изменилось.</span>
              <button type="button" className="ghost" disabled={busy} onClick={() => unignore(suggestion)}>
                {`Вернуть ${suggestion.learning.statement}`}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
